import { useMemo } from "react";
import {
  filterReservationViewByOffer,
  partitionReservations,
  unfinishedCheckoutCount,
} from "@/domain/reservation/rules";
import type { PartnerOffer } from "@/domain/offer/types";
import { CaptureIncidentBanner } from "@/features/reservations/components/CaptureIncidentBanner";
import { ReservationQueueRow } from "@/features/reservations/components/ReservationQueueRow";
import { ReservationRow } from "@/features/reservations/components/ReservationRow";
import { unfinishedCheckoutsNote } from "@/features/reservations/held-places";
import { useReservationAnswers } from "@/features/reservations/hooks/useReservationAnswers";
import { useReservationList } from "@/features/reservations/hooks/useReservationList";

interface OfferReservationsListProps {
  offer: PartnerOffer;
  now: Date;
}

const sectionHeading = "text-xs font-medium uppercase tracking-widest text-muted-foreground";

export function OfferReservationsList({ offer, now }: OfferReservationsListProps) {
  const { reservations } = useReservationList();
  const { confirm, reject, cancel, captureIncident, dismissCaptureIncident } =
    useReservationAnswers();

  const view = useMemo(
    () => filterReservationViewByOffer(partitionReservations(reservations, now), offer.id),
    [reservations, now, offer.id],
  );

  const answeringId = confirm.isPending
    ? confirm.variables
    : reject.isPending
      ? reject.variables
      : null;

  // The panel opens from the occupancy figure, so it answers for that figure:
  // every seat it counts is either a row here or a checkout still in flight.
  // Dropping the settled rows would reintroduce the contradiction the figure's
  // own link was built to end — a count promising rows the list refuses to show.
  const unfinishedCheckouts = unfinishedCheckoutCount(view, offer.reserved);
  const hasReservations =
    view.awaitingAnswer.length > 0 || view.upcoming.length > 0 || view.past.length > 0;

  return (
    <div className="space-y-3">
      {captureIncident !== null && (
        <CaptureIncidentBanner reservationId={captureIncident} onDismiss={dismissCaptureIncident} />
      )}

      {unfinishedCheckouts > 0 && (
        <p className="text-sm text-muted-foreground">
          {unfinishedCheckoutsNote(unfinishedCheckouts)}
        </p>
      )}

      {!hasReservations ? (
        unfinishedCheckouts === 0 && (
          <p className="text-sm text-muted-foreground">
            Zatím k tomuto termínu nejsou žádné rezervace.
          </p>
        )
      ) : (
        <div className="space-y-4">
          {view.awaitingAnswer.length > 0 && (
            <div className="space-y-2">
              <h4 className={sectionHeading}>Čeká na potvrzení</h4>
              <ul className="divide-y divide-border border-y border-border">
                {view.awaitingAnswer.map((reservation) => (
                  <ReservationQueueRow
                    key={reservation.id}
                    reservation={reservation}
                    isAnswering={answeringId === reservation.id}
                    onConfirm={confirm.mutate}
                    onReject={(reservationId, options) => reject.mutate(reservationId, options)}
                  />
                ))}
              </ul>
            </div>
          )}

          {view.upcoming.length > 0 && (
            <div className="space-y-2">
              <h4 className={sectionHeading}>Nadcházející</h4>
              <ul className="divide-y divide-border border-y border-border">
                {view.upcoming.map((reservation) => (
                  <ReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    isCancelling={cancel.isPending && cancel.variables === reservation.id}
                    onCancel={(reservationId, options) => cancel.mutate(reservationId, options)}
                  />
                ))}
              </ul>
            </div>
          )}

          {view.past.length > 0 && (
            <div className="space-y-2">
              <h4 className={sectionHeading}>Uzavřené</h4>
              <ul className="divide-y divide-border border-y border-border">
                {view.past.map((reservation) => (
                  <ReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    isCancelling={cancel.isPending && cancel.variables === reservation.id}
                    onCancel={(reservationId, options) => cancel.mutate(reservationId, options)}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
